import { queryOptions } from "@tanstack/solid-query";
import { downScaleCustomExecutor } from "~/js/nodes/local/logic/downScale";
import {
	changeNodeExecutorOnCustom,
	type nodeNewExecutor
} from "~/js/utilities/changeNodeExecutorOnCustom";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { queryClient } from "~/solidJs/shared/api/queryClient";
import type { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import type { INodeDefinition } from "~/solidJs/shared/helpers/types/INodeDefinition";
import { requestCloudNodeList } from "../network/node-storage";
import {
	CraftsmanApi,
	DalleApi,
	ICLightApi,
	type IGetTaskStatus,
	type IPostTask,
	MidjourneyApi,
	MidjourneyVariateApi,
	RunWayApi,
	Sd3Api,
	SdVideoApi
} from "../network/TaskApi";
import { executor } from "../utilities/executor";
import { readFileCustomExecutor } from "./local/inputs/read-file-executor";
import { chatGptVisionCustomExecuter } from "./local/logic/chatGptVision";
import { dbExecutor } from "./local/logic/dbCustomExecutor";
import { elevenLabsExecutor, elevenLabsGetVoiceIdExecutor } from "./local/logic/elevenlabs";
import { imageCropGlobalId, imageOutpaintGlobalId } from "./local/logic/imageCrop/nodesGlobalIds";
import { clingExecutor, clingExtendExecutor } from "./local/logic/kling";
import {
	midjourneyExecutor,
	midjourneyGetId,
	midjourneyPan,
	midjourneyVariateExecutor,
	midjourneyZoomOut
} from "./local/logic/midjourney";
import { PAD_OUTPAINT_GLOBAL_ID } from "./local/logic/padOutpaint/model";
import { runWayExecutor } from "./local/logic/runway";
import { sdVideoCustomExecutor } from "./local/logic/sdVideo";
import { soraExtractExecutor } from "./local/logic/sora";
import { uploadModel } from "./local/logic/uploadModel";
import { model3dOutputGlobalId } from "./local/outputs/model3dOutput/model3dOutputGlobalId";
import panaramViewerGlobalId from "./local/outputs/panaramViewer/globalID";
import { PromptNullExecutor } from "./local/outputs/promptNull";
import { NodeDefinition, SocketDefinition } from "./node-definition";

interface CloudNodeData extends INodeDefinition {
	multiSocket?: boolean;
	multiViewer?: boolean;
	isNeedToInitViewer?: boolean;
	useOutputViewer?: boolean;
	isNeedToAddClearBtn?: boolean;
	isNeedToCopyOutputs?: boolean;
	version?: string;
	defaultPrice: number;
}

interface CustomExecutorSyncConfig {
	executorType: "sync";
	executor: nodeNewExecutor;
}

interface CustomExecutorAsyncConfig {
	executorType: "async";
	executor: () => Promise<nodeNewExecutor>;
}

type CustomExecutorConfig = CustomExecutorSyncConfig | CustomExecutorAsyncConfig;

interface FetchImplConfig {
	createTaskImpl: IPostTask;
	getTaskStatusImpl: IGetTaskStatus;
}

interface NodeDefinitionWithPromise {
	definition: NodeDefinition;
	promise?: Promise<NodeDefinition>;
}

interface SocketData {
	name: string;
	dataType: string;
	value?: any;
	options?: any;
	optional?: boolean;
}

/**
 * @description contains map <GlobalId, customExecutor>, executor can be sync and hold the particular function, or it can be async and hold the function for fetching the executor module
 */
const nodesToRewriteExecutor: Record<string, CustomExecutorConfig> = {
	// AI model nodes with custom executors
	"Phygital Creator/phygc-rnd-downsample-service": {
		executorType: "sync",
		executor: downScaleCustomExecutor
	},
	"Phygital Creator/phygc-rnd-chat-gpt-vision": {
		executorType: "sync",
		executor: chatGptVisionCustomExecuter
	},
	"Phygital Creator/phygc-rnd-chat-gpt-4o": {
		executorType: "sync",
		executor: chatGptVisionCustomExecuter
	},
	"Phygital Creator/phygc-rnd-chat-gpt-prompt-extend": {
		executorType: "sync",
		executor: chatGptVisionCustomExecuter
	},
	"Phygital Creator/phygc-rnd-chat-gpt-prompt": {
		executorType: "sync",
		executor: chatGptVisionCustomExecuter
	},
	"Phygital Creator/phygc-rnd-chat-gpt-4o-describe": {
		executorType: "sync",
		executor: chatGptVisionCustomExecuter
	},
	"Phygital Creator/phygc-rnd-dreambooth-stable-diff": {
		executorType: "sync",
		executor: dbExecutor
	},
	"Phygital Creator/phygc-rnd-sd3-itv": {
		executorType: "sync",
		executor: sdVideoCustomExecutor
	},
	"Phygital Creator/phygc-rnd-runwayml": {
		executorType: "sync",
		executor: runWayExecutor
	},
	"Phygital Creator/phygc-rnd-elevenlabs-api-ttv": {
		executorType: "sync",
		executor: elevenLabsExecutor
	},
	"Phygital Creator/phygc-rnd-elevenlabs-api-ecv": {
		executorType: "sync",
		executor: elevenLabsGetVoiceIdExecutor
	},
	"Phygital Creator/phygc-rnd-api-kling": {
		executorType: "sync",
		executor: clingExecutor
	},
	"Phygital Creator/phygc-rnd-api-kling-extention": {
		executorType: "sync",
		executor: clingExtendExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney": {
		executorType: "sync",
		executor: midjourneyExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney-variate": {
		executorType: "sync",
		executor: midjourneyVariateExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney-api-variation": {
		executorType: "sync",
		executor: midjourneyVariateExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney-upscale": {
		executorType: "sync",
		executor: midjourneyVariateExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney-api-upscale": {
		executorType: "sync",
		executor: midjourneyVariateExecutor
	},
	"Phygital Creator/phygc-rnd-midjourney-pan": {
		executorType: "sync",
		executor: midjourneyPan
	},
	"Phygital Creator/phygc-rnd-midjourney-api-pan": {
		executorType: "sync",
		executor: midjourneyPan
	},
	"Phygital Creator/phygc-rnd-midjourney-zoomout": {
		executorType: "sync",
		executor: midjourneyZoomOut
	},
	"Phygital Creator/phygc-rnd-midjourney-api-outpaint": {
		executorType: "sync",
		executor: midjourneyZoomOut
	},
	"Phygital Creator/phygc-rnd-midjourney-api-seed": {
		executorType: "sync",
		executor: midjourneyGetId
	},
	"Phygital Creator/phygc-rnd-sora-extract-api": {
		executorType: "sync",
		executor: soraExtractExecutor
	},
	"Phygital Creator/phygc-rnd-upload-model": {
		executorType: "sync",
		executor: uploadModel
	},
	"Import Files": {
		executorType: "sync",
		executor: readFileCustomExecutor
	},
	[model3dOutputGlobalId]: {
		executorType: "async",
		executor: async () => {
			const module = await import("./local/outputs/model3dOutput/customExecutor");
			return module.model3dOutputCustomExecutor;
		}
	},
	[panaramViewerGlobalId]: {
		executorType: "async",
		executor: async () => {
			const module = await import("./local/outputs/panaramViewer/customExecutor");
			return module.panaramViewerCustomExecutor;
		}
	},
	[imageCropGlobalId]: {
		executorType: "async",
		executor: async () => {
			const module = await import("../nodes/local/logic/imageCrop");
			return module.imageCropExecutor;
		}
	},
	[imageOutpaintGlobalId]: {
		executorType: "async",
		executor: async () => {
			const module = await import("../nodes/local/logic/imageCrop");
			return module.imageOutPaintExecutor;
		}
	},
	[PAD_OUTPAINT_GLOBAL_ID]: {
		executorType: "async",
		executor: async () => {
			const module = await import("../nodes/local/logic/padOutpaint");
			return module.padOutpaintExecutor;
		}
	},
	Compose: {
		executorType: "async",
		executor: async () => {
			const module = await import("../nodes/local/logic/compose/composeExecutor");
			return module.composeExecutor;
		}
	},
	PromptNull: {
		executorType: "sync",
		executor: PromptNullExecutor
	}
};

/**
 * holds the Record<node.GlobalId, CustomTaskEndpoint> that will map some nodes to custom backend endpoints
 */
const nodesToRewriteFetchImpl: Record<string, FetchImplConfig> = {
	"Phygital Creator/phygc-rnd-sd3": {
		createTaskImpl: Sd3Api.postStartTraining.bind(Sd3Api),
		getTaskStatusImpl: Sd3Api.getTaskStatus.bind(Sd3Api)
	},
	"Phygital Creator/phygc-rnd-sd3-itv": {
		createTaskImpl: SdVideoApi.postStartTraining.bind(SdVideoApi),
		getTaskStatusImpl: SdVideoApi.getTaskStatus.bind(SdVideoApi)
	},
	"Phygital Creator/phygc-rnd-craftsman": {
		createTaskImpl: CraftsmanApi.postStartTraining.bind(CraftsmanApi),
		getTaskStatusImpl: CraftsmanApi.getTaskStatus.bind(CraftsmanApi)
	},
	"Phygital Creator/phygc-rnd-iclight": {
		createTaskImpl: ICLightApi.postStartTraining.bind(ICLightApi),
		getTaskStatusImpl: ICLightApi.getTaskStatus.bind(ICLightApi)
	},
	"Phygital Creator/phygc-rnd-dalle": {
		createTaskImpl: DalleApi.postStartTraining.bind(DalleApi),
		getTaskStatusImpl: DalleApi.getTaskStatus.bind(DalleApi)
	},
	"Phygital Creator/phygc-rnd-runwayml": {
		createTaskImpl: RunWayApi.postStartTraining.bind(RunWayApi),
		getTaskStatusImpl: RunWayApi.getTaskStatus.bind(RunWayApi)
	},
	"Phygital Creator/phygc-rnd-midjourney": {
		createTaskImpl: MidjourneyApi.postStartTraining.bind(MidjourneyApi),
		getTaskStatusImpl: MidjourneyApi.getTaskStatus.bind(MidjourneyApi)
	},
	"Phygital Creator/phygc-rnd-midjourney-variate": {
		createTaskImpl: (taskData, signal) =>
			MidjourneyVariateApi.postMjStartTraining(taskData, "variate", signal),
		getTaskStatusImpl: MidjourneyVariateApi.getTaskStatus.bind(MidjourneyVariateApi)
	},
	"Phygital Creator/phygc-rnd-midjourney-pan": {
		createTaskImpl: (taskData, signal?: AbortSignal) =>
			MidjourneyVariateApi.postMjStartTraining(taskData, "pan", signal),
		getTaskStatusImpl: MidjourneyVariateApi.getTaskStatus.bind(MidjourneyVariateApi)
	},
	"Phygital Creator/phygc-rnd-midjourney-zoomout": {
		createTaskImpl: (taskData, signal?: AbortSignal) =>
			MidjourneyVariateApi.postMjStartTraining(taskData, "zoomout", signal),
		getTaskStatusImpl: MidjourneyVariateApi.getTaskStatus.bind(MidjourneyVariateApi)
	},
	"Phygital Creator/phygc-rnd-midjourney-upscale": {
		createTaskImpl: (taskData, signal) =>
			MidjourneyVariateApi.postMjStartTraining(taskData, "upscale", signal),
		getTaskStatusImpl: MidjourneyVariateApi.getTaskStatus.bind(MidjourneyVariateApi)
	}
};

export const createNeuralnetsOptions = (
	userGroup: UserSubscriptionPlanEnum | undefined = getUserStore()?.group
) => {
	return queryOptions({
		queryKey: ["neuralnets", userGroup],
		queryFn: () => {
			return neuralNetworksNodeData();
		},
		gcTime: Number.POSITIVE_INFINITY
	});
};

/**
 * @description fetches data from backend and creates list of NodeDefinition
 */
export const fetchNeuralnetsOptions = async (): Promise<
	Record<string, NodeDefinitionWithPromise>
> => {
	const data = await queryClient.fetchQuery(createNeuralnetsOptions());
	return data;
};

/**
 * @description fetches data for NodeDefinition from backend and creates list of NodeDefinition
 */
export async function neuralNetworksNodeData(): Promise<Record<string, NodeDefinitionWithPromise>> {
	const cloudNodeList: CloudNodeData[] = await requestCloudNodeList();

	const definitions: Record<string, NodeDefinitionWithPromise> = {};

	for (const cloudNodeData of cloudNodeList) {
		const definition = new NodeDefinition(
			"Other",
			cloudNodeData.id_global,
			cloudNodeData.name,
			cloudNodeData.description,
			cloudNodeData.averageTimeInSeconds,
			makeSocketGroup(cloudNodeData.inputs),
			makeSocketGroup(cloudNodeData.params),
			makeSocketGroup(cloudNodeData.outputs),
			executor,
			cloudNodeData.useOutputViewer,
			null,
			null,
			null,
			cloudNodeData.id,
			cloudNodeData.multiSocket,
			cloudNodeData.multiViewer,
			cloudNodeData.isNeedToInitViewer,
			null,
			cloudNodeData.isNeedToAddClearBtn,
			cloudNodeData.isNeedToCopyOutputs,
			cloudNodeData.version,
			cloudNodeData.isVisible,
			cloudNodeData.isAvailable,
			nodesToRewriteFetchImpl[cloudNodeData.id_global]?.createTaskImpl,
			nodesToRewriteFetchImpl[cloudNodeData.id_global]?.getTaskStatusImpl as any,
			cloudNodeData.defaultPrice
		);

		definitions[definition.globalId] = handleCustomExecutor(definition);
	}
	return definitions;
}

/**
 * Awaits async custom executor and applies it to the definition
 */
const awaitAsyncCustomExecutor = async (
	definition: NodeDefinition,
	customExecutor: CustomExecutorConfig
): Promise<NodeDefinition> => {
	if (customExecutor.executorType === "async") {
		const asyncExecutor = customExecutor.executor as () => Promise<nodeNewExecutor>;
		const exec = await asyncExecutor();
		changeNodeExecutorOnCustom(definition, exec);
	}
	return definition;
};

/**
 * Handles custom executor for a given definition.
 */
const handleCustomExecutor = (definition: NodeDefinition): NodeDefinitionWithPromise => {
	if (!(definition.globalId in nodesToRewriteExecutor)) {
		return { definition };
	}

	const customExecutor = nodesToRewriteExecutor[definition.globalId];

	// if executor is not code splitted we can simply switch the execution function synchronously
	if (customExecutor.executorType === "sync") {
		const syncExecutor = customExecutor.executor as nodeNewExecutor;
		changeNodeExecutorOnCustom(definition, syncExecutor);
		return { definition };
	}

	// else we need to fetch the new custom executor module and then switch the execution function asynchronously
	return {
		definition,
		promise: awaitAsyncCustomExecutor(definition, customExecutor)
	};
};

/**
 * crates SocketDefinition from data from backend
 * @returns list of SocketDefinition
 */
function makeSocketGroup(socketGroup: SocketData[] | undefined): SocketDefinition[] | undefined {
	if (!socketGroup) return socketGroup;

	return socketGroup.map((socket) => {
		return new SocketDefinition(
			socket.name,
			socket.dataType,
			{
				value: socket.value,
				valueOptions: socket.options
			},
			socket.optional || null
		);
	});
}
